import { ActivityCardDiscover } from "./activity-card-discover";
import { Pagination } from "../../../shared/ui/pagination";

interface ActivityListItem {
  isNew?: boolean;
  id: string;
  slug: string;
  title: string;
  images: string[];
  price: number;
  typePrice?: number;
  rating?: number;
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string };
  segment?: string | null;
  mode?: string;
  /** Venue tags; a restaurant's cuisine names it on the card. */
  tags?: readonly string[] | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface ActivityGridProps {
  items: ActivityListItem[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Leave the numbered pages out; the caller draws its own "Show more". */
  hidePagination?: boolean;
  /** Map view: two columns beside the map, and the card under the pointer tells the map. */
  mapMode?: { activeId: string | null; onHover: (id: string | null) => void };
  /** Badge non-bookable rows "Not bookable" (Restaurants and Spas hubs). */
  markUnbookable?: boolean;
  /** The booking bar's day, time and guests, carried into each bookable card's link. */
  linkSearch?: Record<string, string | number>;
  /** The cuisine chip the guest picked: a card that has it names it. */
  preferCuisine?: string | null;
  /** The list is one city: a cuisine card leaves the city out ("Moroccan"). */
  oneCity?: boolean;
}

export function ActivityGrid({
  items,
  page,
  totalPages,
  onPageChange,
  hidePagination,
  mapMode,
  markUnbookable,
  linkSearch,
  preferCuisine,
  oneCity,
}: ActivityGridProps) {
  return (
    <div>
      <div
        className={
          mapMode
            ? "grid grid-cols-2 gap-4"
            : // Two per row on every phone, 320px included (owner, 2026-10-13).
            "grid grid-cols-2 gap-x-2.5 gap-y-5 min-[400px]:gap-x-3 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4"
        }
      >
        {items.map((a) => (
          // Each card carries an id the map and the list/map glide find it by
          // (the same `stay-hub-card-` prefix the Stays grid uses).
          <div
            key={a.id}
            id={`stay-hub-card-${a.id}`}
            onMouseEnter={mapMode ? () => mapMode.onHover(a.id) : undefined}
            onMouseLeave={mapMode ? () => mapMode.onHover(null) : undefined}
            className="h-full rounded-[22px]"
            style={{
              transition: "box-shadow 200ms cubic-bezier(.2,.8,.2,1), transform 200ms cubic-bezier(.2,.8,.2,1)",
              ...(mapMode?.activeId === a.id ? { boxShadow: "0 0 0 2px #110E39", transform: "translateY(-3px)" } : {}),
            }}
          >
          <ActivityCardDiscover
            slug={a.slug}
            images={a.images}
            title={a.title}
            city={a.city.name}
            citySlug={a.city.slug}
            category={a.category?.name ?? ""}
            categorySlug={a.category?.slug}
            segment={a.segment ?? null}
            tags={a.tags ?? null}
            preferCuisine={preferCuisine}
            oneCity={oneCity}
            mode={a.mode}
            price={a.price}
            isNew={a.isNew === true}
            typePrice={a.typePrice}
            rating={a.rating}
            markUnbookable={markUnbookable}
            linkSearch={linkSearch}
          />
          </div>
        ))}
      </div>
      {hidePagination ? null : <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />}
    </div>
  );
}
